import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { CACHE_VERSION } from "../lib/cacheVersion";
import { toOrgDateKey, toOrgStartISO, toOrgEndISO } from "../lib/timezone";

export interface SpeedToLeadRow {
  setterId: string;
  newLeads: number;
  replied: number;
  pending: number;
  noReply: number;
  medianMins: number | null;
  sub5: number;
  b5_30: number;
  b30_120: number;
  b120plus: number;
}

// Stale-while-revalidate cache. Live ranges (recent date) get short TTL so numbers
// stay reasonably fresh; historical ranges get long TTL since they don't change.
const CACHE_PREFIX = "altara_dm_speed_to_lead_v1";
const TTL_LIVE_MS = 15 * 60 * 1000; // 15 min
const TTL_HISTORICAL_MS = 24 * 60 * 60 * 1000; // 24h

function isLiveRange(endISO: string): boolean {
  // Live = end date is within last 48h (covers "today", "last 7d", current month)
  const end = new Date(endISO).getTime();
  return Date.now() - end < 48 * 60 * 60 * 1000;
}

function readCache(key: string): SpeedToLeadRow[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      version: string;
      timestamp: number;
      ttl: number;
      data: SpeedToLeadRow[];
    };
    if (parsed.version !== CACHE_VERSION) return null;
    if (Date.now() - parsed.timestamp > parsed.ttl) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: SpeedToLeadRow[], ttl: number) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ version: CACHE_VERSION, timestamp: Date.now(), ttl, data })
    );
  } catch {
    // localStorage full or disabled — ignore
  }
}

// Calendar-keyed (not raw toISOString): the login-prefetch warm and the
// DateRangePicker click construct Dates differently (org-tz instants vs
// browser-local midnight) — raw-ISO keys from the two never match, so a warm
// would be dead code. Calendar date keys are construction-independent.
function buildCacheKey(
  organizationId: string,
  start: Date,
  end: Date,
  setterIdsKey: string
): string {
  return `${CACHE_PREFIX}|${organizationId}|${toOrgDateKey(start)}_${toOrgDateKey(end)}|${setterIdsKey}`;
}

function speedTTL(end: Date, timezone: string): number {
  return isLiveRange(toOrgEndISO(end, timezone)) ? TTL_LIVE_MS : TTL_HISTORICAL_MS;
}

// BIGINT/NUMERIC arrive as strings from PostgREST — Number() every count field.
function parseSpeedRows(data: Record<string, unknown>[]): SpeedToLeadRow[] {
  return data.map((r) => ({
    setterId: String(r.setter_id || ""),
    newLeads: Number(r.new_leads || 0),
    replied: Number(r.replied_count || 0),
    pending: Number(r.pending_count || 0),
    noReply: Number(r.no_reply_count || 0),
    // loose == catches both null and undefined from PostgREST
    medianMins: r.median_mins == null ? null : Number(r.median_mins),
    sub5: Number(r.sub_5_count || 0),
    b5_30: Number(r.b_5_30_count || 0),
    b30_120: Number(r.b_30_120_count || 0),
    b120plus: Number(r.b_120_plus_count || 0),
  }));
}

// Login-prefetch warm: fills the same cache the hook reads so a later range
// click is a pure cache hit. Module-level (not a hook). No abortSignal —
// this runs in login background and must complete to be useful.
export async function warmSpeedToLead(
  organizationId: string,
  setterIds: string[],
  dateRange: { start: Date; end: Date },
  timezone: string
): Promise<void> {
  if (!supabase || setterIds.length === 0) return;
  const key = buildCacheKey(organizationId, dateRange.start, dateRange.end, setterIds.join(","));
  if (readCache(key)) return; // already warm — idempotent re-login
  const { data, error } = await supabase.rpc("get_setter_speed_to_lead", {
    p_organization_id: organizationId,
    p_setter_ids: setterIds,
    p_start_date: toOrgStartISO(dateRange.start, timezone),
    p_end_date: toOrgEndISO(dateRange.end, timezone),
  });
  if (error) throw error;
  writeCache(
    key,
    parseSpeedRows((data as Record<string, unknown>[]) || []),
    speedTTL(dateRange.end, timezone)
  );
}

interface Args {
  organizationId: string | null;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone?: string;
}

export function useSpeedToLead({ organizationId, setterIds, dateRange, timezone = "UTC" }: Args) {
  const [rows, setRows] = useState<SpeedToLeadRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startISO = dateRange.start?.toISOString() || null;
  const endISO = dateRange.end?.toISOString() || null;
  const setterIdsKey = setterIds.join(",");

  useEffect(() => {
    if (!organizationId || !startISO || !endISO || setterIds.length === 0 || !supabase) {
      setRows([]);
      return;
    }
    const sb = supabase;
    // Derive from the dep ISO strings (not dateRange object) — no stale closure.
    const startDate = new Date(startISO);
    const endDate = new Date(endISO);
    // Cache key: no windowMode — this hook has no modes. Shared with warmSpeedToLead.
    const cacheKey = buildCacheKey(organizationId, startDate, endDate, setterIdsKey);
    const ttl = speedTTL(endDate, timezone);

    let cancelled = false;
    // Real cancellation: the signal reaches the fetch, so a range change kills
    // the in-flight Postgres query instead of letting it burn the 2-core box.
    const controller = new AbortController();
    setError(null);

    // Stale-while-revalidate: hydrate from cache instantly, then refetch in background.
    const cached = readCache(cacheKey);
    if (cached) {
      setRows(cached);
      setLoading(false); // card renders immediately
    } else {
      setLoading(true); // cold cache — show skeleton
    }

    // Historical range + unexpired cache = the data cannot have changed —
    // skip the background revalidation entirely (zero-RPC warm clicks).
    // Live ranges keep the SWR refetch for freshness.
    if (cached && !isLiveRange(toOrgEndISO(endDate, timezone))) {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    (async () => {
      try {
        // Stagger: let scorecard-gating RPCs land first on the 2-core Postgres box.
        // Cached hydration above (setRows/setLoading) is instant — only the network
        // refetch is delayed. The existing `cancelled` flag makes the wait abort-safe.
        await new Promise((r) => setTimeout(r, 500));
        if (cancelled) return;

        // Org-tz day boundaries (not raw browser-local instants) — matches the
        // warm helper's query window so both writers under one key agree.
        const rpcResult = await sb
          .rpc("get_setter_speed_to_lead", {
            p_organization_id: organizationId,
            p_setter_ids: setterIds,
            p_start_date: toOrgStartISO(startDate, timezone),
            p_end_date: toOrgEndISO(endDate, timezone),
          })
          .abortSignal(controller.signal);

        if (cancelled) return;
        if (rpcResult.error) throw rpcResult.error;

        const built = parseSpeedRows((rpcResult.data as Record<string, unknown>[]) || []);

        if (cancelled) return;

        setRows(built);
        writeCache(cacheKey, built, ttl);
      } catch (e) {
        if ((e as Error)?.name === "AbortError") return;
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organizationId, startISO, endISO, setterIdsKey, timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  // setterIds excluded from deps in favor of setterIdsKey (stable string) to avoid re-fetch on array identity change

  return { rows, loading, error };
}
